import React, {useState, useCallback, FormEvent} from 'react';
import {useParams, useSearchParams, Link} from 'react-router-dom';
import {useMutation} from '@tanstack/react-query';
import {Circle, CircleCheck} from 'lucide-react';
import {AuthService} from '@/modules/auth/services/authService';
import Card from '@/shared/components/Card';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import {PasswordInput} from '@/modules/auth/components/PasswordInput';

interface PasswordErrors {
    passwordLetters: boolean;
    passwordNumber: boolean;
    passwordLength: boolean;
}

const Verify: React.FC = () => {
    const {token} = useParams<{token: string}>();
    const [searchParams] = useSearchParams();
    const ref = searchParams.get('ref');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({
        passwordLetters: false,
        passwordNumber: false,
        passwordLength: false,
    });
    const [verified, setVerified] = useState(false);

    // Password validation function
    const validatePassword = useCallback((password: string): PasswordErrors => {
        const passwordUppercaseReg = /[A-Z]/;
        const passwordLowercaseReg = /[a-z]/;
        const passwordNumberReg = /[\d]/;

        return {
            passwordLetters: !passwordUppercaseReg.test(password) || !passwordLowercaseReg.test(password),
            passwordNumber: !passwordNumberReg.test(password),
            passwordLength: password.length <= 8,
        };
    }, []);

    // Mutation for setting password
    const setPasswordMutation = useMutation({
        mutationFn: ({token, password}: {token: string; password: string}) => AuthService.setPassword(token, password),
        onSuccess: () => {
            setVerified(true);
        },
        onError: () => {
            // Handle error silently - error state is shown via mutation.isError
        },
    });

    const handlePasswordChange = (value: string) => {
        setPassword(value);
        setPasswordErrors(validatePassword(value));
    };

    const passwordsMatch = password.length > 0 && password === confirmPassword;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        if (!token) return;

        setHasSubmitted(true);
        const errors = validatePassword(password);
        setPasswordErrors(errors);

        const hasErrors = Object.values(errors).some((error) => error);
        if (hasErrors || !passwordsMatch) {
            return;
        }

        setPasswordMutation.mutate({token, password});
    };

    const hasPasswordErrors =
        passwordErrors.passwordLetters || passwordErrors.passwordNumber || passwordErrors.passwordLength;

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md" padding="lg">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-mld-text-primary mb-2">Invalid Link</h2>
                        <p className="text-gray-600 mb-4">The verification link is invalid or expired.</p>
                        <Link
                            to="/login"
                            className="px-4 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors cursor-pointer">
                            Go to Login
                        </Link>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            {setPasswordMutation.isPending && <LoadingSpinner />}

            <Card className="w-full max-w-md" padding="lg">
                {!verified ? (
                    <>
                        <div className="text-center mb-6">
                            <h2 className="text-2xl font-bold text-mld-text-primary mb-2">Set Your Password</h2>
                            <p className="text-gray-600">
                                This password will be used to log in going forward. Use the eye icon to double-check
                                what you typed before submitting.
                            </p>
                        </div>

                        {setPasswordMutation.isError && (
                            <div className="mb-4 p-3 bg-mld-danger/5 border border-mld-danger/20 rounded-md">
                                <p className="text-sm text-mld-danger">
                                    Verification failed. Please try again or contact support.
                                </p>
                            </div>
                        )}
                        {hasSubmitted && hasPasswordErrors && (
                            <div className="mb-4 p-3 bg-mld-warning/5 border border-mld-warning/30 rounded-md">
                                <p className="text-sm text-mld-warning">
                                    Please meet all password requirements before continuing.
                                </p>
                            </div>
                        )}
                        {hasSubmitted && !hasPasswordErrors && !passwordsMatch && (
                            <div className="mb-4 p-3 bg-mld-warning/5 border border-mld-warning/30 rounded-md">
                                <p className="text-sm text-mld-warning">Passwords don&apos;t match — please check both fields.</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-sm font-medium text-mld-text-primary mb-2">
                                    Password
                                </label>
                                <PasswordInput
                                    id="password"
                                    value={password}
                                    onChange={(e) => handlePasswordChange(e.target.value)}
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors ${
                                        hasSubmitted && hasPasswordErrors
                                            ? 'border-mld-danger/30 focus:ring-mld-danger focus:border-mld-danger'
                                            : 'border-gray-300'
                                    }`}
                                    placeholder="Enter your password"
                                    autoComplete="new-password"
                                    required
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="confirm-password"
                                    className="block text-sm font-medium text-mld-text-primary mb-2">
                                    Confirm Password
                                </label>
                                <PasswordInput
                                    id="confirm-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-mld-primary outline-none transition-colors ${
                                        hasSubmitted && !hasPasswordErrors && !passwordsMatch
                                            ? 'border-mld-danger/30 focus:ring-mld-danger focus:border-mld-danger'
                                            : 'border-gray-300'
                                    }`}
                                    placeholder="Re-enter your password"
                                    autoComplete="new-password"
                                    required
                                />
                            </div>

                            {/* Password Requirements */}
                            <div className="space-y-2">
                                <h4 className="text-sm font-medium text-mld-text-primary">Password Requirements:</h4>
                                <div className="space-y-1">
                                    <div className="flex items-center space-x-2">
                                        {passwordErrors.passwordLength || password === '' ? (
                                            <Circle className="h-4 w-4 text-gray-300" aria-hidden="true" />
                                        ) : (
                                            <CircleCheck className="h-4 w-4 text-mld-success" aria-hidden="true" />
                                        )}
                                        <span
                                            className={`text-sm ${
                                                passwordErrors.passwordLength || password === ''
                                                    ? 'text-gray-500'
                                                    : 'text-mld-success'
                                            }`}>
                                            At least 9 characters long
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {passwordErrors.passwordLetters || password === '' ? (
                                            <Circle className="h-4 w-4 text-gray-300" aria-hidden="true" />
                                        ) : (
                                            <CircleCheck className="h-4 w-4 text-mld-success" aria-hidden="true" />
                                        )}
                                        <span
                                            className={`text-sm ${
                                                passwordErrors.passwordLetters || password === ''
                                                    ? 'text-gray-500'
                                                    : 'text-mld-success'
                                            }`}>
                                            Include at least 1 uppercase and lowercase letter
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {passwordErrors.passwordNumber || password === '' ? (
                                            <Circle className="h-4 w-4 text-gray-300" aria-hidden="true" />
                                        ) : (
                                            <CircleCheck className="h-4 w-4 text-mld-success" aria-hidden="true" />
                                        )}
                                        <span
                                            className={`text-sm ${
                                                passwordErrors.passwordNumber || password === ''
                                                    ? 'text-gray-500'
                                                    : 'text-mld-success'
                                            }`}>
                                            Include at least 1 number
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={setPasswordMutation.isPending}
                                className="w-full py-3 px-4 bg-mld-primary text-white font-semibold rounded-lg hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                {setPasswordMutation.isPending ? 'Setting Password...' : 'Set Password'}
                            </button>
                        </form>
                    </>
                ) : (
                    <div className="text-center">
                        <div className="mb-6">
                            <CircleCheck className="mx-auto mb-4 h-16 w-16 text-mld-success" aria-hidden="true" />
                            <h2 className="text-2xl font-bold text-mld-text-primary mb-2">Account Verified!</h2>
                            <p className="text-gray-600 mb-6">
                                Your account has been verified and your password has been set successfully.
                            </p>
                        </div>
                        <Link
                            to={ref ? `/login?ref=${ref}` : '/login'}
                            className="inline-block py-3 px-6 bg-mld-primary text-white font-semibold rounded-lg hover:bg-mld-primary/90 focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-2 transition-colors">
                            Continue to Login
                        </Link>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default Verify;
